import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../utils';
import { partnerApiPath } from '../../apiPath';
import { partnerHttpClient } from '../../httpClient';
import { LocalDateTimeSchema } from './_shared/LocalDateTimeSchema';

const ParticipationStatusFilterSchema = z
  .enum(['PENDING_ACCEPTANCE', 'REJECTED', 'PENDING_SHIPMENT', 'SHIPPED'])
  .describe('ParticipationStatusFilterSchema');

export type ParticipationStatusFilter = z.infer<typeof ParticipationStatusFilterSchema>;

const ParticipationContentTypeSchema = z
  .enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])
  .describe('ParticipationContentTypeSchema');

export type ParticipationContentType = z.infer<typeof ParticipationContentTypeSchema>;

const ParticipationSnsChannelSchema = z
  .object({
    type: z.string(),
    url: z.string(),
  })
  .describe('ParticipationSnsChannelSchema');

const ParticipationGenderSchema = z
  .union([z.enum(['MALE', 'FEMALE']), z.enum(['M', 'F']), z.null()])
  .transform((value) => {
    if (value === 'M') return 'MALE' as const;
    if (value === 'F') return 'FEMALE' as const;
    return value;
  })
  .describe('ParticipationGenderSchema');

const ParticipationCuratorProfileSchema = z
  .object({
    curatorId: z.string(),
    nickname: z.string(),
    profileImage: z.string().nullable().optional(),
    gender: ParticipationGenderSchema,

    age: z.number().nullable(),
    curatorShopUrl: z.string().nullable().optional(),
    snsChannels: z.array(ParticipationSnsChannelSchema),
    snapProfileId: z.string().nullable().optional(),
    wishlistCount: z.number(),
    // BE 가 캠페인 현황 응답에 브랜드 찜 여부를 제공하면 사용. 미제공 시 service 가 false 로 보정. (QAT-39544)
    likedByBrand: z.boolean().optional(),
  })
  .describe('ParticipationCuratorProfileSchema');

export type ParticipationCuratorProfile = z.infer<typeof ParticipationCuratorProfileSchema>;

const ParticipationRowSchema = z
  .object({
    participationId: z.number(),
    curatorProfile: ParticipationCuratorProfileSchema.nullable(),
    proposedAt: LocalDateTimeSchema.nullable().optional(),
    confirmedAt: LocalDateTimeSchema.nullable().optional(),
    responseDeadlineAt: LocalDateTimeSchema.nullable().optional(),
    respondedAt: LocalDateTimeSchema.nullable().optional(),
    rejectionReasons: z.array(z.string()).nullable().optional(),
    selectedAt: LocalDateTimeSchema.nullable().optional(),
    shippingDeadlineAt: LocalDateTimeSchema.nullable().optional(),
    shippedAt: LocalDateTimeSchema.nullable().optional(),
    selectedContentType: ParticipationContentTypeSchema.nullable().optional(),
    messageToBrand: z.string().nullable().optional(),

    orderId: z.string().nullable().optional(),
    orderSheetUrls: z.array(z.string()).nullable().optional(),
  })
  .describe('ParticipationRowSchema');

export type ParticipationRow = z.infer<typeof ParticipationRowSchema>;

const GetCampaignSeedingParticipationsRequestSchema = z
  .object({
    campaignId: z.union([z.number(), z.string()]),
    statusFilter: ParticipationStatusFilterSchema,
    page: z.number().int().min(0).optional(),
    size: z.number().int().min(1).max(100).optional(),
  })
  .describe('GetCampaignSeedingParticipationsRequestSchema');

export type GetCampaignSeedingParticipationsRequest = z.infer<
  typeof GetCampaignSeedingParticipationsRequestSchema
>;

const GetCampaignSeedingParticipationsResponseDataSchema = z
  .object({
    list: z.array(ParticipationRowSchema),
    hasNext: z.boolean().optional(),
  })
  .describe('GetCampaignSeedingParticipationsResponseDataSchema');

const GetCampaignSeedingParticipationsResponseSchema = createApiResponseSchema(
  GetCampaignSeedingParticipationsResponseDataSchema,
);

export type GetCampaignSeedingParticipationsResponse = z.infer<
  typeof GetCampaignSeedingParticipationsResponseSchema
>;

export const getCampaignSeedingParticipations = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCampaignSeedingParticipationsRequestSchema),
    async ({ campaignId, ...query }: GetCampaignSeedingParticipationsRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.get<GetCampaignSeedingParticipationsResponse>(
          partnerApiPath.getCampaignSeedingParticipations(campaignId),
          query,
        ),
      ),
    validateApiResponse(GetCampaignSeedingParticipationsResponseSchema),
  ),
);
